import { useState, useEffect, useCallback, useMemo } from "react";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Progress } from "../components/ui/progress";
import {
  Target, Plus, Users, BarChart3, Star, TrendingUp, CheckCircle2,
  Brain, Zap, MessageSquare, Award, ArrowRight, AlertTriangle, BookOpen,
  Lightbulb, UserCheck, Activity, Send, ThumbsUp, Flag, Sparkles
} from "lucide-react";
import axios from "axios";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import { HelpTip } from "../components/HelpTip";

const NINEBOX_CONFIG = [
  { box: 7, label: "Enigma", row: 0, col: 0, color: "bg-teal-100 text-teal-800 border-teal-300" },
  { box: 8, label: "Futuro Lider", row: 0, col: 1, color: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  { box: 9, label: "Estrella", row: 0, col: 2, color: "bg-violet-100 text-violet-800 border-violet-300" },
  { box: 4, label: "Dilema", row: 1, col: 0, color: "bg-orange-100 text-orange-800 border-orange-300" },
  { box: 5, label: "Profesional", row: 1, col: 1, color: "bg-blue-100 text-blue-800 border-blue-300" },
  { box: 6, label: "Alto Rendimiento", row: 1, col: 2, color: "bg-cyan-100 text-cyan-800 border-cyan-300" },
  { box: 1, label: "Riesgo", row: 2, col: 0, color: "bg-red-100 text-red-800 border-red-300" },
  { box: 2, label: "Efectivo", row: 2, col: 1, color: "bg-amber-100 text-amber-800 border-amber-300" },
  { box: 3, label: "Experto", row: 2, col: 2, color: "bg-yellow-100 text-yellow-800 border-yellow-300" },
];

const FEEDBACK_TYPES = [
  { value: "recognition", label: "Reconocimiento", icon: ThumbsUp, color: "bg-emerald-100 text-emerald-700" },
  { value: "improvement", label: "Area de Mejora", icon: Flag, color: "bg-amber-100 text-amber-700" },
  { value: "suggestion", label: "Sugerencia", icon: Lightbulb, color: "bg-blue-100 text-blue-700" },
];

const ax = () => {
  const token = localStorage.getItem("token");
  return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
};

const Performance = () => {
  useAuth();
  const [evaluations, setEvaluations] = useState([]);
  const [objectives, setObjectives] = useState([]);
  const [ninebox, setNinebox] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [stats, setStats] = useState(null);
  const [aiInsights, setAiInsights] = useState(null);
  const [leaderDashboard, setLeaderDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  const [tab, setTab] = useState("overview");
  const [workCenterFilter, setWorkCenterFilter] = useState("all");

  // Dialogs
  const [showEvalDialog, setShowEvalDialog] = useState(false);
  const [showObjDialog, setShowObjDialog] = useState(false);
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [selectedEval, setSelectedEval] = useState(null);

  // Forms
  const [newEval, setNewEval] = useState({ employee_id: "", eval_type: "360", period: "", performance_score: 5, potential_score: 5 });
  const [newObj, setNewObj] = useState({ employee_id: "", title: "", description: "", due_date: "", key_results: "" });
  const [newFeedback, setNewFeedback] = useState({ from_employee_id: "", to_employee_id: "", type: "recognition", message: "", category: "general" });
  const [completeData, setCompleteData] = useState({ performance_score: 5, potential_score: 5, strengths: "", areas_improvement: "", comments: "" });

  const workCenters = useMemo(() => {
    const seen = new Map();
    employees.forEach(employee => {
      if (employee.work_center_id && !seen.has(employee.work_center_id)) {
        seen.set(employee.work_center_id, { id: employee.work_center_id, name: employee.work_center_name });
      }
    });
    return Array.from(seen.values());
  }, [employees]);
  const selectableEmployees = useMemo(() => workCenterFilter === "all" ? employees : employees.filter(employee => employee.work_center_id === workCenterFilter), [employees, workCenterFilter]);

  const fetchData = useCallback(async () => {
    try {
      const opts = ax();
      const query = workCenterFilter !== "all" ? `?work_center_id=${workCenterFilter}` : "";
      const [evalRes, objRes, nbRes, empRes, fbRes, statsRes, leaderRes] = await Promise.all([
        axios.get(`${API}/evaluations${query}`, opts).catch(() => ({ data: [] })),
        axios.get(`${API}/objectives${query}`, opts).catch(() => ({ data: [] })),
        axios.get(`${API}/ninebox${query}`, opts).catch(() => ({ data: [] })),
        axios.get(`${API}/employees`, opts).catch(() => ({ data: [] })),
        axios.get(`${API}/feedback`, opts).catch(() => ({ data: [] })),
        axios.get(`${API}/performance/stats${query}`, opts).catch(() => ({ data: null })),
        axios.get(`${API}/performance/leader-dashboard${query}`, opts).catch(() => ({ data: null })),
      ]);
      setEvaluations(evalRes.data || []);
      setObjectives(objRes.data || []);
      setNinebox(nbRes.data || []);
      setEmployees(empRes.data || []);
      setFeedback(fbRes.data || []);
      setStats(statsRes.data);
      setLeaderDashboard(leaderRes.data);
    } catch { toast.error("Error al cargar datos"); } finally { setLoading(false); }
  }, [workCenterFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getEmpName = (id) => { const e = employees.find(x => x.employee_id === id); return e ? `${e.first_name} ${e.last_name}` : id || ""; };
  const filteredFeedback = useMemo(() => {
    if (workCenterFilter === "all") return feedback;
    return feedback.filter(item => item.work_center_id === workCenterFilter || employees.find(employee => employee.employee_id === item.to_employee_id)?.work_center_id === workCenterFilter);
  }, [employees, feedback, workCenterFilter]);

  const handleCreateEval = async () => {
    if (!newEval.employee_id || !newEval.period) { toast.error("Empleado y período requeridos"); return; }
    try {
      await axios.post(`${API}/evaluations`, newEval, ax());
      toast.success("Evaluación creada");
      setShowEvalDialog(false);
      setNewEval({ employee_id: "", eval_type: "360", period: "", performance_score: 5, potential_score: 5 });
      fetchData();
    } catch { toast.error("Error al crear evaluación"); }
  };

  const handleCreateObj = async () => {
    if (!newObj.employee_id || !newObj.title) { toast.error("Empleado y título requeridos"); return; }
    try {
      await axios.post(`${API}/objectives`, newObj, ax());
      toast.success("Objetivo creado");
      setShowObjDialog(false);
      setNewObj({ employee_id: "", title: "", description: "", due_date: "", key_results: "" });
      fetchData();
    } catch { toast.error("Error al crear objetivo"); }
  };

  const handleCreateFeedback = async () => {
    if (!newFeedback.to_employee_id || !newFeedback.message) { toast.error("Destinatario y mensaje requeridos"); return; }
    try {
      const payload = {
        ...newFeedback,
        from_name: getEmpName(newFeedback.from_employee_id),
        to_name: getEmpName(newFeedback.to_employee_id)
      };
      await axios.post(`${API}/feedback`, payload, ax());
      toast.success("Feedback enviado");
      setShowFeedbackDialog(false);
      setNewFeedback({ from_employee_id: "", to_employee_id: "", type: "recognition", message: "", category: "general" });
      fetchData();
    } catch { toast.error("Error al enviar feedback"); }
  };

  const handleCompleteEval = async () => {
    if (!selectedEval) return;
    try {
      await axios.put(`${API}/evaluations/${selectedEval.evaluation_id}/complete`, completeData, ax());
      toast.success("Evaluación completada");
      setShowCompleteDialog(false);
      setSelectedEval(null);
      fetchData();
    } catch { toast.error("Error al completar evaluación"); }
  };

  const handleUpdateProgress = async (objId, progress) => {
    try {
      await axios.put(`${API}/objectives/${objId}`, { progress }, ax());
      fetchData();
    } catch { toast.error("Error"); }
  };

  const loadAI = async () => {
    setAiLoading(true);
    try {
      const query = workCenterFilter !== "all" ? `?work_center_id=${workCenterFilter}` : "";
      const res = await axios.get(`${API}/performance/ai-insights${query}`, ax());
      setAiInsights(res.data);
      toast.success("Insights IA generados");
    } catch { toast.error("Error al generar insights IA"); } finally { setAiLoading(false); }
  };

  if (loading) return <Layout title="Desempeño"><div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" /></div></Layout>;

  return (
    <Layout title="Gestión de Desempeño" subtitle="Evaluaciones 360, liderazgo, centros de trabajo y planes con IA"
      actions={<div className="flex gap-2 flex-wrap">
        <Select value={workCenterFilter} onValueChange={setWorkCenterFilter}>
          <SelectTrigger className="w-[220px]" data-testid="performance-work-center-filter"><SelectValue placeholder="Filtrar por centro" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los centros</SelectItem>
            {workCenters.map(center => <SelectItem key={center.id} value={center.id}>{center.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={() => setShowFeedbackDialog(true)} data-testid="create-feedback-btn"><MessageSquare className="w-4 h-4 mr-2" />Feedback</Button>
        <Button variant="outline" onClick={() => setShowObjDialog(true)} data-testid="create-obj-btn"><Target className="w-4 h-4 mr-2" />Objetivo</Button>
        <Button onClick={() => setShowEvalDialog(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-eval-btn"><Plus className="w-4 h-4 mr-2" />Evaluación</Button>
      </div>}>

      {/* KPIs */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-6" data-testid="performance-kpis">
          {[
            { label: "Evaluaciones", value: stats.total_evaluations, sub: `${stats.completed_evaluations} completadas`, icon: Star, color: "bg-amber-100 text-amber-600" },
            { label: "Prom. Desempeño", value: `${stats.avg_performance}/10`, sub: `Potencial: ${stats.avg_potential}`, icon: TrendingUp, color: "bg-emerald-100 text-emerald-600" },
            { label: "Objetivos", value: stats.total_objectives, sub: `${stats.completed_objectives} logrados`, icon: Target, color: "bg-blue-100 text-blue-600" },
            { label: "Progreso OKR", value: `${stats.obj_progress}%`, sub: `${stats.pending_objectives} pendientes`, icon: CheckCircle2, color: "bg-violet-100 text-violet-600" },
            { label: "Feedback", value: stats.total_feedback, sub: "continuo", icon: MessageSquare, color: "bg-pink-100 text-pink-600" },
          ].map((s, i) => (
            <Card key={i} className="border-slate-200"><CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${s.color}`}><s.icon className="w-5 h-5" /></div>
                <div><p className="text-xl font-bold text-slate-900">{s.value}</p><p className="text-[11px] text-slate-500">{s.label}</p><p className="text-[10px] text-slate-400">{s.sub}</p></div>
              </div>
            </CardContent></Card>
          ))}
        </div>
      )}

      {leaderDashboard && (
        <Card className="border-slate-200 mb-6 bg-gradient-to-br from-white to-rose-50/40" data-testid="leader-dashboard-card">
          <CardContent className="p-5 grid lg:grid-cols-[1.1fr_0.9fr] gap-5">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <UserCheck className="w-5 h-5 text-rose-600" />
                <h3 className="text-lg font-bold text-slate-900">Cockpit del líder</h3>
                <HelpTip title="Cómo usar el cockpit del líder">
                  <p>Te muestra el tamaño del equipo, pendientes de evaluación y distribución por centro.</p>
                  <p>Es especialmente útil para supervisores y líderes de operación multisede.</p>
                </HelpTip>
              </div>
              <p className="text-sm text-slate-600">{leaderDashboard.team_size || 0} reporte(s) directos y {leaderDashboard.pending_reviews || 0} evaluación(es) pendientes.</p>
              <div className="grid sm:grid-cols-3 gap-3 mt-4">
                <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200"><p className="text-xs text-slate-400 uppercase">Equipo</p><p className="text-2xl font-bold text-slate-900">{leaderDashboard.team_size || 0}</p></div>
                <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200"><p className="text-xs text-slate-400 uppercase">Pendientes</p><p className="text-2xl font-bold text-slate-900">{leaderDashboard.pending_reviews || 0}</p></div>
                <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200"><p className="text-xs text-slate-400 uppercase">Centros</p><p className="text-2xl font-bold text-slate-900">{leaderDashboard.team_by_center?.length || 0}</p></div>
              </div>
            </div>
            <div className="space-y-3">
              {(leaderDashboard.team_by_center || []).length === 0 ? <p className="text-sm text-slate-500">Sin distribución por centro todavía.</p> : leaderDashboard.team_by_center.map(center => (
                <div key={center.work_center_name} className="p-3 rounded-xl border border-slate-200 bg-white">
                  <div className="flex items-center justify-between gap-3">
                    <p className="font-semibold text-slate-900">{center.work_center_name}</p>
                    <Badge className="bg-sky-100 text-sky-700">{center.employees} pers.</Badge>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Progreso promedio de objetivos: {center.avg_progress}%</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-6 flex-wrap h-auto gap-1" data-testid="performance-tabs">
          <TabsTrigger value="overview">Resumen</TabsTrigger>
          <TabsTrigger value="evaluations"><Star className="w-3.5 h-3.5 mr-1" />Evaluaciones</TabsTrigger>
          <TabsTrigger value="objectives"><Target className="w-3.5 h-3.5 mr-1" />OKRs</TabsTrigger>
          <TabsTrigger value="ninebox"><BarChart3 className="w-3.5 h-3.5 mr-1" />NineBox</TabsTrigger>
          <TabsTrigger value="feedback"><MessageSquare className="w-3.5 h-3.5 mr-1" />Feedback</TabsTrigger>
          <TabsTrigger value="ai" className="text-violet-600"><Brain className="w-3.5 h-3.5 mr-1" />IA</TabsTrigger>
        </TabsList>

        {/* OVERVIEW */}
        <TabsContent value="overview">
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Recent Evaluations */}
            <Card className="border-slate-200">
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><Star className="w-4 h-4 text-amber-500" />Últimas Evaluaciones</CardTitle></CardHeader>
              <CardContent>
                {evaluations.length === 0 ? <p className="text-sm text-slate-400 text-center py-4">Sin evaluaciones aún</p> : (
                  <div className="space-y-2">
                    {evaluations.slice(0, 5).map(ev => (
                      <div key={ev.evaluation_id} className="flex items-center gap-3 p-2.5 rounded-lg border border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`overview-eval-${ev.evaluation_id}`}>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">{getEmpName(ev.employee_id)}</p>
                          <p className="text-xs text-slate-500">{ev.period} | {ev.eval_type}</p>
                        </div>
                        <div className="text-center px-2"><p className="text-xs text-slate-400">Desemp.</p><p className="text-sm font-bold text-emerald-600">{ev.performance_score || "-"}</p></div>
                        <Badge className={ev.status === "completed" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>{ev.status === "completed" ? "Completada" : ev.status === "draft" ? "Borrador" : "Enviada"}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
            {/* Objectives Progress */}
            <Card className="border-slate-200">
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><Target className="w-4 h-4 text-blue-500" />Objetivos Activos</CardTitle></CardHeader>
              <CardContent>
                {objectives.length === 0 ? <p className="text-sm text-slate-400 text-center py-4">Sin objetivos aún</p> : (
                  <div className="space-y-3">
                    {objectives.filter(o => o.status !== "completed").slice(0, 5).map(obj => (
                      <div key={obj.objective_id} className="space-y-1" data-testid={`overview-obj-${obj.objective_id}`}>
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-slate-900 truncate flex-1">{obj.title}</p>
                          <span className="text-xs text-slate-500 ml-2">{obj.progress || 0}%</span>
                        </div>
                        <p className="text-xs text-slate-500">{getEmpName(obj.employee_id)}</p>
                        <Progress value={obj.progress || 0} className="h-1.5" />
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
            {/* Recent Feedback */}
            <Card className="border-slate-200 lg:col-span-2">
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><MessageSquare className="w-4 h-4 text-pink-500" />Feedback Reciente</CardTitle></CardHeader>
              <CardContent>
                {filteredFeedback.length === 0 ? <p className="text-sm text-slate-400 text-center py-4">Sin feedback aún</p> : (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {filteredFeedback.slice(0, 6).map(fb => {
                      const ft = FEEDBACK_TYPES.find(t => t.value === fb.type) || FEEDBACK_TYPES[0];
                      return (
                        <div key={fb.feedback_id} className="p-3 rounded-xl border border-slate-200 hover:shadow-sm transition-all" data-testid={`fb-${fb.feedback_id}`}>
                          <div className="flex items-center gap-2 mb-2">
                            <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${ft.color}`}><ft.icon className="w-3.5 h-3.5" /></div>
                            <div><p className="text-xs font-semibold text-slate-700">{fb.from_name || "Anónimo"}</p><p className="text-[10px] text-slate-400">{ft.label}</p></div>
                          </div>
                          <p className="text-xs text-slate-600 line-clamp-2">{fb.message}</p>
                          <p className="text-[10px] text-slate-400 mt-2">Para: {fb.to_name}</p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* EVALUATIONS */}
        <TabsContent value="evaluations">
          {evaluations.length === 0 ? (
            <Card><CardContent className="p-8 text-center text-slate-500" data-testid="evals-empty"><Star className="w-12 h-12 mx-auto mb-3 text-slate-300" /><p className="font-medium">No hay evaluaciones</p><p className="text-sm mt-1">Crea tu primera evaluación 360 para comenzar</p></CardContent></Card>
          ) : (
            <div className="space-y-3">
              {evaluations.map(ev => (
                <Card key={ev.evaluation_id} className="border-slate-200 hover:shadow-sm transition-all" data-testid={`eval-${ev.evaluation_id}`}>
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center text-white font-bold shrink-0">
                      {getEmpName(ev.employee_id).split(" ").map(n => n[0]).join("").slice(0, 2)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-900">{getEmpName(ev.employee_id)}</p>
                      <p className="text-sm text-slate-500">Período: {ev.period} | Tipo: {ev.eval_type === "360" ? "360°" : ev.eval_type === "self" ? "Autoevaluación" : "Jefe Directo"}</p>
                      {ev.work_center_name && <p className="text-xs text-sky-600 mt-1">Centro: {ev.work_center_name}</p>}
                    </div>
                    <div className="text-center px-3"><p className="text-xs text-slate-400">Desempeño</p><p className="text-lg font-bold text-emerald-600">{ev.performance_score || "-"}</p></div>
                    <div className="text-center px-3"><p className="text-xs text-slate-400">Potencial</p><p className="text-lg font-bold text-blue-600">{ev.potential_score || "-"}</p></div>
                    <Badge className={ev.status === "completed" ? "bg-emerald-100 text-emerald-700" : ev.status === "submitted" ? "bg-blue-100 text-blue-700" : "bg-amber-100 text-amber-700"}>
                      {ev.status === "completed" ? "Completada" : ev.status === "submitted" ? "Enviada" : "Borrador"}
                    </Badge>
                    {ev.status !== "completed" && (
                      <Button size="sm" variant="outline" onClick={() => { setSelectedEval(ev); setCompleteData({ performance_score: ev.performance_score || 5, potential_score: ev.potential_score || 5, strengths: "", areas_improvement: "", comments: "" }); setShowCompleteDialog(true); }}
                        data-testid={`complete-eval-${ev.evaluation_id}`}>
                        <CheckCircle2 className="w-3.5 h-3.5 mr-1" />Completar
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* OKR OBJECTIVES */}
        <TabsContent value="objectives">
          {objectives.length === 0 ? (
            <Card><CardContent className="p-8 text-center text-slate-500" data-testid="objs-empty"><Target className="w-12 h-12 mx-auto mb-3 text-slate-300" /><p className="font-medium">No hay objetivos</p></CardContent></Card>
          ) : (
            <div className="space-y-3">
              {objectives.map(obj => (
                <Card key={obj.objective_id} className="border-slate-200 hover:shadow-sm transition-all" data-testid={`obj-${obj.objective_id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex-1 min-w-0 mr-4">
                        <p className="font-semibold text-slate-900">{obj.title}</p>
                        <p className="text-sm text-slate-500">{getEmpName(obj.employee_id)}{obj.due_date ? ` | Vence: ${obj.due_date}` : ""}</p>
                        {obj.work_center_name && <p className="text-xs text-sky-600 mt-1">Centro: {obj.work_center_name}</p>}
                        {obj.key_results && <p className="text-xs text-slate-400 mt-1">KR: {obj.key_results}</p>}
                      </div>
                      <Badge className={obj.status === "completed" ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"}>
                        {obj.status === "completed" ? "Logrado" : "En Progreso"}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-3">
                      <Progress value={obj.progress || 0} className="h-2 flex-1" />
                      <span className="text-sm font-bold text-slate-700 w-12 text-right">{obj.progress || 0}%</span>
                    </div>
                    {obj.status !== "completed" && (
                      <div className="flex gap-1 mt-3">
                        {[25, 50, 75, 100].map(p => (
                          <Button key={p} size="sm" variant={obj.progress >= p ? "default" : "outline"} className="text-xs h-7"
                            onClick={() => handleUpdateProgress(obj.objective_id, p)} data-testid={`obj-progress-${obj.objective_id}-${p}`}>{p}%</Button>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* NINEBOX */}
        <TabsContent value="ninebox">
          <Card data-testid="ninebox-matrix" className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2"><BarChart3 className="w-5 h-5 text-violet-600" />Matriz NineBox - Gestión del Talento</CardTitle>
              <p className="text-xs text-slate-500 mt-1">Clasifica empleados según desempeño (horizontal) y potencial (vertical) para decisiones estratégicas de talento</p>
            </CardHeader>
            <CardContent>
              {ninebox.length === 0 ? (
                <div className="text-center py-12 text-slate-500" data-testid="ninebox-empty">
                  <BarChart3 className="w-14 h-14 mx-auto mb-3 text-slate-300" />
                  <p className="font-medium">Sin datos para NineBox</p>
                  <p className="text-sm mt-1">Completa evaluaciones con scores de desempeño y potencial para poblar la matriz</p>
                </div>
              ) : (
                <div>
                  {/* Y axis label */}
                  <div className="flex items-stretch gap-3">
                    <div className="flex flex-col justify-between py-2 text-xs text-slate-500 w-16 shrink-0">
                      <span className="font-semibold">Alto</span>
                      <span>Medio</span>
                      <span>Bajo</span>
                    </div>
                    <div className="flex-1">
                      <div className="grid grid-cols-3 gap-2">
                        {NINEBOX_CONFIG.map(cfg => {
                          const boxEmployees = ninebox.filter(n => n.box === cfg.box);
                          return (
                            <div key={cfg.box} className={`${cfg.color} rounded-xl p-3 min-h-[110px] border transition-all hover:shadow-sm`} data-testid={`ninebox-${cfg.box}`}>
                              <p className="text-xs font-bold mb-1">{cfg.label}</p>
                              <p className="text-[10px] opacity-70 mb-2">{boxEmployees.length} empleado{boxEmployees.length !== 1 ? "s" : ""}</p>
                              {boxEmployees.slice(0, 4).map(e => (
                                <div key={e.employee_id} className="flex items-center gap-1.5 mb-1">
                                  <div className="w-5 h-5 rounded-full bg-white/60 flex items-center justify-center text-[8px] font-bold">{e.name?.split(" ").map(n => n[0]).join("").slice(0, 2)}</div>
                                  <span className="text-[10px] truncate">{e.name}</span>
                                </div>
                              ))}
                              {boxEmployees.length > 4 && <p className="text-[10px] opacity-60 mt-1">+{boxEmployees.length - 4} más</p>}
                            </div>
                          );
                        })}
                      </div>
                      <div className="flex justify-between mt-2 text-xs text-slate-400 px-1">
                        <span>Bajo Desempeño</span><span>Medio</span><span>Alto Desempeño</span>
                      </div>
                    </div>
                  </div>
                  {/* Legend */}
                  <div className="mt-4 p-3 rounded-xl bg-slate-50 border border-slate-200">
                    <p className="text-xs font-semibold text-slate-700 mb-2">Resumen del Talento</p>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="text-center"><p className="text-lg font-bold text-violet-700">{ninebox.filter(n => n.box >= 7).length}</p><p className="text-[10px] text-slate-500">Alto Potencial</p></div>
                      <div className="text-center"><p className="text-lg font-bold text-blue-700">{ninebox.filter(n => [5, 6].includes(n.box)).length}</p><p className="text-[10px] text-slate-500">Rendimiento Sólido</p></div>
                      <div className="text-center"><p className="text-lg font-bold text-red-700">{ninebox.filter(n => n.box <= 2).length}</p><p className="text-[10px] text-slate-500">Requieren Atención</p></div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* FEEDBACK CONTINUO */}
        <TabsContent value="feedback">
          {filteredFeedback.length === 0 ? (
            <Card><CardContent className="p-8 text-center text-slate-500" data-testid="feedback-empty">
              <MessageSquare className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="font-medium">Sin feedback aún</p>
              <p className="text-sm mt-1">Fomenta la retroalimentación continua entre colaboradores</p>
              <Button onClick={() => setShowFeedbackDialog(true)} className="mt-4 bg-emerald-600 hover:bg-emerald-700"><Send className="w-4 h-4 mr-2" />Enviar Primer Feedback</Button>
            </CardContent></Card>
          ) : (
            <div className="space-y-3">
              {filteredFeedback.map(fb => {
                const ft = FEEDBACK_TYPES.find(t => t.value === fb.type) || FEEDBACK_TYPES[0];
                return (
                  <Card key={fb.feedback_id} className="border-slate-200 hover:shadow-sm transition-all" data-testid={`feedback-card-${fb.feedback_id}`}>
                    <CardContent className="p-4 flex items-start gap-4">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${ft.color}`}><ft.icon className="w-5 h-5" /></div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="text-sm font-semibold text-slate-900">{fb.from_name || "Anónimo"}</p>
                          <ArrowRight className="w-3 h-3 text-slate-400" />
                          <p className="text-sm font-medium text-slate-700">{fb.to_name}</p>
                        </div>
                        <Badge className={`${ft.color} text-[10px] mb-2`}>{ft.label}</Badge>
                        <p className="text-sm text-slate-600">{fb.message}</p>
                        {fb.work_center_name && <p className="text-[10px] text-sky-600 mt-2">Centro: {fb.work_center_name}</p>}
                        {fb.created_at && <p className="text-[10px] text-slate-400 mt-2">{new Date(fb.created_at).toLocaleDateString("es-MX", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</p>}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* AI INSIGHTS */}
        <TabsContent value="ai">
          <Card className="border-slate-200 mb-4">
            <CardContent className="p-5 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-slate-900 flex items-center gap-2"><Sparkles className="w-5 h-5 text-violet-500" />Insights de Desempeño con IA</h3>
                <p className="text-xs text-slate-500 mt-1">Planes de desarrollo, predicción de potencial, y recomendaciones de capacitación generadas por GPT-5.2</p>
              </div>
              <Button onClick={loadAI} disabled={aiLoading} className="bg-violet-600 hover:bg-violet-700" data-testid="load-performance-ai-btn">
                {aiLoading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" /> : <Brain className="w-4 h-4 mr-2" />}
                {aiLoading ? "Analizando..." : "Generar Insights"}
              </Button>
            </CardContent>
          </Card>
          {aiInsights ? (
            <div className="space-y-4">
              {/* Development Plans */}
              {aiInsights.development_plans?.length > 0 && (
                <Card className="border-slate-200">
                  <CardHeader><CardTitle className="text-base flex items-center gap-2"><BookOpen className="w-4 h-4 text-emerald-600" />Planes de Desarrollo Personalizados</CardTitle></CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {aiInsights.development_plans.map((plan, i) => (
                        <div key={i} className="p-4 rounded-xl border border-slate-200 bg-white hover:shadow-sm transition-all" data-testid={`dev-plan-${i}`}>
                          <p className="font-semibold text-slate-900 mb-2 flex items-center gap-2"><UserCheck className="w-4 h-4 text-emerald-500" />{plan.employee}</p>
                          <div className="grid md:grid-cols-2 gap-3">
                            <div className="p-2.5 rounded-lg bg-emerald-50">
                              <p className="text-[10px] uppercase text-emerald-600 font-semibold mb-1">Fortalezas</p>
                              <p className="text-xs text-slate-700">{plan.strengths}</p>
                            </div>
                            <div className="p-2.5 rounded-lg bg-amber-50">
                              <p className="text-[10px] uppercase text-amber-600 font-semibold mb-1">Areas de Mejora</p>
                              <p className="text-xs text-slate-700">{plan.areas}</p>
                            </div>
                          </div>
                          {plan.actions?.length > 0 && (
                            <div className="mt-2 p-2.5 rounded-lg bg-blue-50">
                              <p className="text-[10px] uppercase text-blue-600 font-semibold mb-1">Acciones Recomendadas</p>
                              <ul className="space-y-1">{plan.actions.map((a, j) => <li key={j} className="text-xs text-slate-700 flex items-start gap-1.5"><ArrowRight className="w-3 h-3 text-blue-500 mt-0.5 shrink-0" />{a}</li>)}</ul>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
              {/* Team Insights */}
              {aiInsights.team_insights?.length > 0 && (
                <Card className="border-slate-200">
                  <CardHeader><CardTitle className="text-base flex items-center gap-2"><Activity className="w-4 h-4 text-blue-600" />Insights del Equipo</CardTitle></CardHeader>
                  <CardContent><div className="space-y-2">
                    {aiInsights.team_insights.map((ins, i) => (
                      <div key={i} className="flex items-start gap-2 p-3 rounded-lg border border-slate-100"><Lightbulb className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" /><p className="text-sm text-slate-700">{ins}</p></div>
                    ))}
                  </div></CardContent>
                </Card>
              )}
              {/* Recommendations */}
              {aiInsights.recommendations?.length > 0 && (
                <Card className="border-l-4 border-l-violet-500">
                  <CardHeader><CardTitle className="text-base flex items-center gap-2"><Award className="w-4 h-4 text-violet-600" />Recomendaciones Estratégicas</CardTitle></CardHeader>
                  <CardContent><div className="space-y-2">
                    {aiInsights.recommendations.map((rec, i) => (
                      <div key={i} className="flex items-start gap-2 p-3 rounded-lg bg-violet-50"><ArrowRight className="w-4 h-4 text-violet-500 mt-0.5 shrink-0" /><p className="text-sm text-slate-700">{rec}</p></div>
                    ))}
                  </div></CardContent>
                </Card>
              )}
              {/* Risk Areas */}
              {aiInsights.risk_areas?.length > 0 && (
                <Card className="border-l-4 border-l-red-500">
                  <CardHeader><CardTitle className="text-base flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-red-500" />Areas de Riesgo</CardTitle></CardHeader>
                  <CardContent><div className="space-y-2">
                    {aiInsights.risk_areas.map((risk, i) => (
                      <div key={i} className="flex items-start gap-2 p-3 rounded-lg bg-red-50"><AlertTriangle className="w-3.5 h-3.5 text-red-400 mt-0.5 shrink-0" /><p className="text-sm text-slate-700">{risk}</p></div>
                    ))}
                  </div></CardContent>
                </Card>
              )}
              {aiInsights.team_focus?.length > 0 && (
                <Card className="border-l-4 border-l-sky-500">
                  <CardHeader><CardTitle className="text-base flex items-center gap-2"><Users className="w-4 h-4 text-sky-600" />Foco para el líder</CardTitle></CardHeader>
                  <CardContent><div className="space-y-2">{aiInsights.team_focus.map((item, index) => <div key={index} className="flex items-start gap-2 p-3 rounded-lg bg-sky-50"><ArrowRight className="w-4 h-4 text-sky-500 mt-0.5 shrink-0" /><p className="text-sm text-slate-700">{item}</p></div>)}</div></CardContent>
                </Card>
              )}
              {aiInsights.center_insights?.length > 0 && (
                <Card className="border-l-4 border-l-cyan-500">
                  <CardHeader><CardTitle className="text-base flex items-center gap-2"><BarChart3 className="w-4 h-4 text-cyan-600" />Hallazgos por centro</CardTitle></CardHeader>
                  <CardContent><div className="space-y-2">{aiInsights.center_insights.map((item, index) => <div key={index} className="flex items-start gap-2 p-3 rounded-lg bg-cyan-50"><ArrowRight className="w-4 h-4 text-cyan-500 mt-0.5 shrink-0" /><p className="text-sm text-slate-700">{item}</p></div>)}</div></CardContent>
                </Card>
              )}
            </div>
          ) : (
            <Card className="border-slate-200"><CardContent className="p-12 text-center text-slate-500">
              <Brain className="w-16 h-16 mx-auto mb-4 text-violet-200" />
              <p className="font-medium text-lg text-slate-700">Inteligencia Artificial para Desempeño</p>
              <p className="text-sm mt-2 max-w-md mx-auto">Genera planes de desarrollo personalizados, identifica áreas de riesgo y obtén recomendaciones estratégicas basadas en tus datos de evaluación</p>
            </CardContent></Card>
          )}
        </TabsContent>
      </Tabs>

      {/* === CREATE EVALUATION DIALOG === */}
      <Dialog open={showEvalDialog} onOpenChange={setShowEvalDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nueva Evaluación</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Empleado *</Label>
              <Select value={newEval.employee_id} onValueChange={v => setNewEval({ ...newEval, employee_id: v })}>
                <SelectTrigger data-testid="eval-employee"><SelectValue placeholder="Seleccionar empleado" /></SelectTrigger>
                <SelectContent>{selectableEmployees.map(e => <SelectItem key={e.employee_id} value={e.employee_id}>{e.first_name} {e.last_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Tipo</Label>
                <Select value={newEval.eval_type} onValueChange={v => setNewEval({ ...newEval, eval_type: v })}>
                  <SelectTrigger data-testid="eval-type"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="360">360°</SelectItem><SelectItem value="self">Autoevaluación</SelectItem><SelectItem value="manager">Jefe directo</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Período *</Label><Input value={newEval.period} onChange={e => setNewEval({ ...newEval, period: e.target.value })} placeholder="2026-Q1" data-testid="eval-period" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Desempeño (1-10)</Label><Input type="number" min={1} max={10} value={newEval.performance_score} onChange={e => setNewEval({ ...newEval, performance_score: parseInt(e.target.value) || 5 })} data-testid="eval-performance" /></div>
              <div><Label>Potencial (1-10)</Label><Input type="number" min={1} max={10} value={newEval.potential_score} onChange={e => setNewEval({ ...newEval, potential_score: parseInt(e.target.value) || 5 })} data-testid="eval-potential" /></div>
            </div>
            <Button onClick={handleCreateEval} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="save-eval-btn">Crear Evaluación</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* === CREATE OBJECTIVE DIALOG === */}
      <Dialog open={showObjDialog} onOpenChange={setShowObjDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nuevo Objetivo (OKR)</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Empleado *</Label>
              <Select value={newObj.employee_id} onValueChange={v => setNewObj({ ...newObj, employee_id: v })}>
                <SelectTrigger data-testid="obj-employee"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                <SelectContent>{selectableEmployees.map(e => <SelectItem key={e.employee_id} value={e.employee_id}>{e.first_name} {e.last_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Objetivo *</Label><Input value={newObj.title} onChange={e => setNewObj({ ...newObj, title: e.target.value })} placeholder="Aumentar ventas en 20%" data-testid="obj-title" /></div>
            <div><Label>Resultados Clave</Label><Textarea rows={2} value={newObj.key_results} onChange={e => setNewObj({ ...newObj, key_results: e.target.value })} placeholder="KR1: ..., KR2: ..." data-testid="obj-kr" /></div>
            <div><Label>Fecha Límite</Label><Input type="date" value={newObj.due_date} onChange={e => setNewObj({ ...newObj, due_date: e.target.value })} data-testid="obj-due" /></div>
            <Button onClick={handleCreateObj} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="save-obj-btn">Crear Objetivo</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* === FEEDBACK DIALOG === */}
      <Dialog open={showFeedbackDialog} onOpenChange={setShowFeedbackDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Enviar Feedback</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>De (opcional)</Label>
              <Select value={newFeedback.from_employee_id} onValueChange={v => setNewFeedback({ ...newFeedback, from_employee_id: v })}>
                <SelectTrigger data-testid="fb-from"><SelectValue placeholder="Anónimo" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="anonymous">Anónimo</SelectItem>
                  {selectableEmployees.map(e => <SelectItem key={e.employee_id} value={e.employee_id}>{e.first_name} {e.last_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Para *</Label>
              <Select value={newFeedback.to_employee_id} onValueChange={v => setNewFeedback({ ...newFeedback, to_employee_id: v })}>
                <SelectTrigger data-testid="fb-to"><SelectValue placeholder="Seleccionar empleado" /></SelectTrigger>
                <SelectContent>{selectableEmployees.map(e => <SelectItem key={e.employee_id} value={e.employee_id}>{e.first_name} {e.last_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Tipo</Label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                {FEEDBACK_TYPES.map(ft => (
                  <button key={ft.value} onClick={() => setNewFeedback({ ...newFeedback, type: ft.value })}
                    className={`p-2.5 rounded-lg border-2 text-center transition-all ${newFeedback.type === ft.value ? "border-emerald-500 bg-emerald-50" : "border-slate-200 hover:border-slate-300"}`}
                    data-testid={`fb-type-${ft.value}`}>
                    <ft.icon className={`w-5 h-5 mx-auto mb-1 ${newFeedback.type === ft.value ? "text-emerald-600" : "text-slate-400"}`} />
                    <p className="text-xs font-medium">{ft.label}</p>
                  </button>
                ))}
              </div>
            </div>
            <div><Label>Mensaje *</Label><Textarea rows={3} value={newFeedback.message} onChange={e => setNewFeedback({ ...newFeedback, message: e.target.value })} placeholder="Escribe tu feedback..." data-testid="fb-message" /></div>
            <Button onClick={handleCreateFeedback} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="save-fb-btn"><Send className="w-4 h-4 mr-2" />Enviar Feedback</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* === COMPLETE EVALUATION DIALOG === */}
      <Dialog open={showCompleteDialog} onOpenChange={setShowCompleteDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Completar Evaluación</DialogTitle></DialogHeader>
          {selectedEval && (
            <div className="space-y-4">
              <p className="text-sm text-slate-500">Empleado: <strong>{getEmpName(selectedEval.employee_id)}</strong> | Período: {selectedEval.period}</p>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Desempeño Final (1-10)</Label><Input type="number" min={1} max={10} value={completeData.performance_score} onChange={e => setCompleteData({ ...completeData, performance_score: parseInt(e.target.value) || 5 })} data-testid="complete-perf" /></div>
                <div><Label>Potencial Final (1-10)</Label><Input type="number" min={1} max={10} value={completeData.potential_score} onChange={e => setCompleteData({ ...completeData, potential_score: parseInt(e.target.value) || 5 })} data-testid="complete-pot" /></div>
              </div>
              <div><Label>Fortalezas</Label><Textarea rows={2} value={completeData.strengths} onChange={e => setCompleteData({ ...completeData, strengths: e.target.value })} placeholder="Principales fortalezas del colaborador..." data-testid="complete-strengths" /></div>
              <div><Label>Areas de Mejora</Label><Textarea rows={2} value={completeData.areas_improvement} onChange={e => setCompleteData({ ...completeData, areas_improvement: e.target.value })} placeholder="Areas donde puede mejorar..." data-testid="complete-areas" /></div>
              <div><Label>Comentarios</Label><Textarea rows={2} value={completeData.comments} onChange={e => setCompleteData({ ...completeData, comments: e.target.value })} placeholder="Comentarios adicionales..." data-testid="complete-comments" /></div>
              <Button onClick={handleCompleteEval} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="save-complete-btn"><CheckCircle2 className="w-4 h-4 mr-2" />Completar Evaluación</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Performance;
